import { Sparkles, Crown, Heart, Compass, Mic, BookOpen, Coins, Brain, TrendingUp, Target, ShieldCheck, HandCoins, Lightbulb, Megaphone, Wrench, BookOpenCheck, HandHeart } from "lucide-react";

/**
 * Source of truth for native program hubs inside JJU.
 * Each hub renders pillars, modules, who-it's-for, optional external link,
 * and an "express interest" form (POST /api/program-interest).
 */
export const PROGRAMS = {
  "confidence-arise": {
    key: "confidence-arise",
    name: "Confidence Arise",
    tagline: "Build the business He's already named.",
    overline: "A business builder information hub",
    heroBlurb:
      "A Christ-centered hub for believers building businesses. Free downloadable e-books, practical teaching, mindset shifts, and done-with-you services to get you from idea to up-and-running.",
    accent: "gold",
    heroImage:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    externalUrl: "https://confidencearise.com",
    externalLabel: "Open the hub →",
    pillars: [
      { icon: BookOpenCheck, title: "Knowledge", body: "Practical business teaching — branding, offers, operations, marketing, and money — taught plainly, with Kingdom values baked in." },
      { icon: Brain, title: "Mindset", body: "Renew how you think about work, worth, and wealth so the business can hold the weight of what God is calling you to." },
      { icon: BookOpen, title: "Free resources", body: "Downloadable e-books, templates, checklists, and guides on the Confidence Arise site. Take what you need." },
      { icon: HandHeart, title: "Services", body: "Done-with-you and done-for-you offerings to get from idea to running business — without losing your peace along the way." },
    ],
    modules: [
      { num: "01", title: "The Foundation", body: "Identity, mindset, calling. Why you're building before what you're building. Most businesses fail here, quietly." },
      { num: "02", title: "The Idea & The Offer", body: "Naming the business, clarifying the offer, and pricing it without apology." },
      { num: "03", title: "Brand & Voice", body: "Showing up so the right people find you — story, visuals, message, and the courage to be seen." },
      { num: "04", title: "Systems & Operations", body: "Practical setup: LLC, taxes, tools, contracts, and how to actually get paid on time." },
      { num: "05", title: "Marketing & Audience", body: "Building an audience God can grow with — content, email, partnerships, real relationships." },
      { num: "06", title: "Scale & Stewardship", body: "Hiring, delegating, and stewarding profit Kingdom-first as the work gets bigger than you." },
    ],
    whoFor: [
      "The believer with an idea that won't leave them alone",
      "The side-hustler ready to make it a full thing",
      "The owner who's hit a ceiling and needs both strategy and a mindset shift",
      "Anyone hungry for practical, Kingdom-aligned business teaching",
    ],
    faqs: [
      { q: "Is this just for women?", a: "No. Confidence Arise is for every believer building a business — men and women, side-hustlers and seasoned owners. It's a starting point for anyone called to build something with the Lord." },
      { q: "Is this a course or a website?", a: "Both. The Confidence Arise hub is free to browse — e-books, downloads, templates, and articles. We also offer paid services for people who want help actually getting their business set up and growing." },
      { q: "What does it cost?", a: "The information hub and downloads are free. Services (coaching, done-with-you setup, strategy work) are priced individually depending on what you need." },
      { q: "How do I get started?", a: "Easiest path: open the hub and download whatever resonates. If you want services or a personal next step, express interest below and we'll reach out." },
    ],
  },

  "million-dollar-mindset": {
    key: "million-dollar-mindset",
    name: "Million Dollar Mindset",
    tagline: "Kingdom wealth begins between your ears.",
    overline: "For believers building generational impact",
    heroBlurb:
      "A renewing-of-the-mind program for believers ready to break poverty patterns, steward wealth biblically, and build something that outlives them. Not prosperity gospel — Kingdom economics.",
    accent: "burgundy",
    heroImage:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    externalUrl: null,
    externalLabel: "",
    pillars: [
      { icon: Brain, title: "Renew the mind", body: "Romans 12:2 isn't a slogan — it's the operating system upgrade. We rewire the inherited scripts about money." },
      { icon: ShieldCheck, title: "Steward, don't hoard", body: "It's all His. We learn to manage what God entrusts with wisdom, generosity, and joy." },
      { icon: TrendingUp, title: "Build to bless", body: "Income streams, savings, investments — engineered with eternity in view. Excellence, not extravagance." },
      { icon: HandCoins, title: "Give to grow", body: "Generosity isn't the leftover — it's the discipline that breaks the spirit of mammon and unlocks Kingdom flow." },
    ],
    modules: [
      { num: "01", title: "The Money Story", body: "Map the scripts you inherited — fear, scarcity, shame — and let the Word edit them out." },
      { num: "02", title: "Budget Like a Steward", body: "A zero-based budget rooted in Proverbs. Practical templates. Real numbers. No condemnation." },
      { num: "03", title: "Debt & Deliverance", body: "A faith-and-math plan to clear consumer debt and keep it gone." },
      { num: "04", title: "Earn More Without Selling Your Soul", body: "Honoring God in how you earn — careers, side hustles, ministry-aligned income." },
      { num: "05", title: "Invest with Eternity in Mind", body: "Saving, investing, and building wealth that funds Kingdom assignments, not just retirement." },
      { num: "06", title: "Generational Wealth", body: "Estate, legacy, and the gift of leaving an inheritance to your children's children (Prov 13:22)." },
    ],
    whoFor: [
      "The believer drowning in debt and ready for a way out",
      "The earner doing well but tipped by the spirit of mammon",
      "The parent who wants to break the family money curse",
      "The entrepreneur called to build something that funds the Kingdom",
    ],
    faqs: [
      { q: "Is this prosperity gospel?", a: "No. We do not believe God owes you a Bentley because you tithed. We believe God calls believers to be wise stewards, generous givers, and excellent builders — and that He blesses faithfulness in His timing, His way." },
      { q: "Do I need a finance background?", a: "Not at all. The program meets you where you are — whether your bank account is empty, full, or somewhere in between. The math is plain English." },
      { q: "Will there be a cohort or is it self-paced?", a: "We're launching with a guided cohort track once we have at least 25 committed members on the waitlist. Express your interest below to be first in line." },
    ],
  },
};

export const PROGRAM_LIST = Object.values(PROGRAMS);

export const PROGRAM_ICONS = {
  "confidence-arise": Lightbulb,
  "million-dollar-mindset": Coins,
};

export { Target, BookOpen, Sparkles, Crown, Heart, Compass, Mic, Megaphone, Wrench };

